// Handle chunk loading errors that occur after deployment (when old chunks no longer exist).
// This happens when a user has the app loaded and we deploy a new version - their cached
// HTML references chunk hashes that no longer exist on the server.

export const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Loading chunk \d+ failed/,
  /Loading CSS chunk \d+ failed/,
  /Unable to preload CSS/,
];

export const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

const RELOAD_ATTEMPTED_KEY = 'chunkErrorReloadAttempted';

/**
 * Attempts a single page reload to recover from a stale chunk error.
 * Returns false if a reload was already attempted (to prevent infinite loops).
 */
export const attemptChunkErrorReload = (): boolean => {
  try {
    if (sessionStorage.getItem(RELOAD_ATTEMPTED_KEY)) {
      return false;
    }

    console.warn('Chunk load error detected, reloading page to get latest version');
    sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, 'true');
    window.location.reload();
    return true;
  } catch {
    // sessionStorage may be unavailable (e.g., Safari private browsing)
    const url = new URL(window.location.href);
    if (url.searchParams.has('chunkReloadAttempted')) {
      return false;
    }
    console.warn('Chunk load error detected, reloading page (sessionStorage unavailable)');
    url.searchParams.set('chunkReloadAttempted', 'true');
    window.location.href = url.toString();
    return true;
  }
};

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    if (!attemptChunkErrorReload()) {
      console.error('Chunk load error persists after reload:', event.reason);
    }
  }
});

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error ?? event.message)) {
    event.preventDefault();
    if (!attemptChunkErrorReload()) {
      console.error('Chunk load error persists after reload:', event.error ?? event.message);
    }
  }
});

// Clear the reload flag on successful page load so future deployments can trigger reloads
try {
  sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY);
} catch {
  // Ignore if sessionStorage is unavailable
}
